import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Icon } from '@framework/ui/atoms/Icon/Icon';
import { FirebaseAuthenticationClient } from '../firebase/auth/FirebaseAuthenticationClient';

/** online 復帰時の自動リロードをタブごとにずらす幅(ミリ秒)。 */
const ReloadJitterMilliSeconds = 3000;

/**
 * 通信起因で認証状態を確定できなかったときに、画面全体で状況を案内する。
 *
 * この表示に至る時点で Firebase 側にはサインイン済みのユーザーが残っているため、
 * ログイン画面ではなく再読み込みを促す。回線が戻った時点(online)では自動的に
 * 再読み込みして復帰させる。online は遷移時にのみ発火するため繰り返しにはならない。
 *
 * この画面はアプリのルーティング全体を置き換えるため、ログイン・ログアウトの画面にも
 * 到達できない。再読み込みでは回復しない恒久的な失敗(通信の遮断・レート制限など)で
 * 閉じ込められないよう、サインアウトの導線を併せて置く。signOut はローカル処理のみで
 * 完結するため、通信できない状態でも成功する。
 */
export const AuthUnavailableNotice: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | undefined;
    const scheduleReload = () => {
      // 回線がばたついて online が続けて発火しても、リロードは一度だけ予約する。
      if (timerId !== undefined) return;
      // 複数タブがこの画面にいると online は全タブへ同時に配送される。そのまま即座に
      // リロードすると全タブが同時にトークンのリフレッシュを要求し、リトライに jitter を
      // 入れて避けたのと同じ共倒れになるため、ここでもタイミングをばらす。
      timerId = setTimeout(() => window.location.reload(), Math.random() * ReloadJitterMilliSeconds);
    };

    window.addEventListener('online', scheduleReload);
    return () => {
      if (timerId !== undefined) clearTimeout(timerId);
      window.removeEventListener('online', scheduleReload);
    };
  }, []);

  return (
    <div className="absolute left-0 top-0 flex h-screen w-screen flex-col items-center justify-center">
      <div className="flex flex-col items-center px-6 text-center">
        <Icon name="balus" color="brand" size="brand-xl" />
        <div className="mt-3 font-bold text-brand">Balus 2.0</div>

        <h2 className="my-5 text-2xl font-bold">{t('framework.networkError.title')}</h2>

        <p>{t('framework.networkError.message')}</p>

        <button type="button" className="mt-5 text-brand underline" onClick={() => window.location.reload()}>
          {t('framework.networkError.reloadLabel')}
        </button>

        <button
          type="button"
          className="mt-3 text-sm text-gray-500 underline"
          onClick={() =>
            FirebaseAuthenticationClient.signOut()
              .then(() => navigate('/login', { replace: true }))
              .catch((error) => Sentry.captureException(error))
          }
        >
          {t('framework.networkError.signOutLabel')}
        </button>
      </div>
    </div>
  );
};
